import React, { useState } from 'react';
import { Plus, Phone, Car, User } from 'lucide-react';
import ItineraryContactModal from '../../../ItineraryTable/modals/ItineraryContactModal';

const ImportantContactsCard = ({ voucher, onSaveContacts }) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const contacts = voucher?.contact_details || [];

  const handleOpenAdd = () => {
    setSelectedContact(null);
    setIsContactModalOpen(true);
  };

  const handleOpenEdit = (contact = null) => {
    setSelectedContact(contact || (contacts.length > 0 ? contacts[0] : null));
    setIsContactModalOpen(true);
  };

  const handleSaveContact = (contactObj, isEdit) => {
    const current = [...(voucher?.contact_details || [])];
    const matchIdx = current.findIndex(c => (c.id || c._id) === contactObj.id);
    let updated;
    if (matchIdx >= 0) {
      updated = [...current];
      updated[matchIdx] = { ...updated[matchIdx], ...contactObj };
    } else {
      updated = [...current, contactObj];
    }
    if (onSaveContacts) {
      onSaveContacts(updated);
    }
  };

  const handleDeleteContact = (contactId) => {
    const current = [...(voucher?.contact_details || [])];
    const updated = current.filter(c => (c.id || c._id) !== contactId);
    if (onSaveContacts) {
      onSaveContacts(updated);
    }
  };

  return (
    <>
      <div className="vc-card">
        <div className="vc-card-head flex items-center justify-between gap-2 flex-wrap pb-2 border-b border-slate-100">
          <h3 className="flex items-center gap-2 text-sm font-medium text-slate-600 tracking-tight">
            <span className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
              <Phone size={15} className="text-[#067978]" />
            </span>
            <span>Important Contacts</span>
          </h3>

          <button
            onClick={handleOpenAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs font-medium transition-all shadow-2xs active:scale-95 cursor-pointer shrink-0"
            type="button"
          >
            <Plus size={14} /> Add Contact
          </button>
        </div>

        {contacts.length === 0 ? (
          <div className="text-sm text-[#8a9aa5] py-4 px-3 flex items-center gap-2.5 bg-slate-50/70 rounded-xl border border-dashed border-slate-200 mt-2">
            <Phone size={18} className="text-slate-400 shrink-0" />
            <span className="text-xs font-medium text-slate-500">
              No important contacts added yet.
            </span>
          </div>
        ) : (
          <div className="space-y-2 mt-2">
            {contacts.slice(0, 3).map((c, i) => {
              const displayRole = c.label || c.role || "Support";
              const roleLower = String(displayRole).toLowerCase();
              const isDriver = roleLower.includes('driver') || roleLower.includes('chauffeur') || roleLower.includes('cab') || roleLower.includes('taxi');

              return (
                <div
                  key={c.id || c._id || i}
                  onClick={() => handleOpenEdit(c)}
                  className="flex items-center justify-between p-2.5 bg-slate-50/70 hover:bg-slate-100/80 border border-slate-200/80 rounded-xl transition-all group cursor-pointer shadow-2xs"
                  title="Click to view or edit contact"
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 border border-teal-100/80">
                      {isDriver ? (
                        <Car size={15} />
                      ) : (
                        <User size={15} />
                      )}
                    </div>
                    {/* Outer card shows ONLY: Name, Role, Phone Number */}
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-800 truncate group-hover:text-[#067978] transition-colors leading-tight">
                        {c.name || 'Unnamed Contact'}
                      </p>
                      <p className="text-[11px] text-slate-600 font-medium truncate mt-0.5 leading-tight">
                        {displayRole}
                      </p>
                      {c.number && (
                        <p className="text-[10px] text-slate-400 font-normal font-mono truncate mt-0.5 leading-tight">
                          {c.number}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.number && (
                      <a
                        href={`tel:${c.number}`}
                        onClick={(e) => e.stopPropagation()}
                        className="px-2.5 py-1 rounded-full border border-[#067978] text-[#067978] bg-white hover:bg-teal-50/80 text-[11px] font-medium inline-flex items-center gap-1 transition-all shadow-2xs active:scale-95"
                        title={`Call ${c.name}`}
                      >
                        <Phone size={11} />
                        <span>Call</span>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}

            {contacts.length > 3 && (
              <button
                type="button"
                onClick={() => handleOpenEdit(contacts[0])}
                className="w-full py-1 text-center text-xs font-medium text-[#067978] hover:underline cursor-pointer"
              >
                +{contacts.length - 3} more contact{contacts.length - 3 > 1 ? 's' : ''} • View All
              </button>
            )}
          </div>
        )}
      </div>

      <ItineraryContactModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          setSelectedContact(null);
        }}
        mode="general"
        itinerary={voucher?.itinerary || []}
        tourStartDate={voucher?.tour_info?.start_date}
        contacts={contacts}
        initialContact={selectedContact}
        onSaveContact={handleSaveContact}
        onDeleteContact={handleDeleteContact}
      />
    </>
  );
};

export default ImportantContactsCard;
